import jwtDecode from 'jwt-decode';
import { verify, sign } from 'jsonwebtoken';
import { ACCESS_TOKEN_NAME, ACCESS_TOKEN_USER, REFRESH_TOKEN_NAME } from '../const';
import { getMimicUser } from './mimic';

// Kundvy ("logga in som") stashes a customer impersonation token under
// ACCESS_TOKEN_USER and navigates the tab to /client/*. localStorage is
// shared across tabs on the same origin, so unconditionally preferring the
// USER slot (as the previous implementation did) meant every admin tab
// started reading the customer token the moment kundvy was opened
// anywhere — and, worse, the passive session watchdog would nuke the
// admin's own valid session when the stale kundvy token ticked past its
// `exp`. Preferring USER only on /client/* keeps kundvy working inside
// its tab while leaving admin tabs on the admin's own session.
const isKundvyPath = () => {
  try {
    return typeof window !== 'undefined'
      && typeof window.location !== 'undefined'
      && String(window.location.pathname || '').startsWith('/client');
  } catch (_) {
    return false;
  }
};

const readSlot = (name) => {
  try {
    return window.localStorage.getItem(name);
  } catch (_) {
    return null;
  }
};

export const getActiveTokenSlot = () => {
  if (isKundvyPath() && readSlot(ACCESS_TOKEN_USER)) {
    return ACCESS_TOKEN_USER;
  }
  return ACCESS_TOKEN_NAME;
};

export const getActiveToken = () => readSlot(getActiveTokenSlot());

export const getStoredRefreshToken = () => readSlot(REFRESH_TOKEN_NAME);

// Reshapes the localStorage mimic record into a JWT-payload-lookalike so the
// helpers below can transparently return the mimicked user's identity fields
// (id / role / user_type / customer_id / email) whenever an admin is mimicking.
const getMimicPayloadOverlay = () => {
  const m = getMimicUser();
  if (!m || !m.id || !m.type) return null;
  return {
    id: m.id,
    email: m.email,
    role: m.role,
    user_type: m.type,
    customer_id: m.type === 'customer' ? m.customerId : undefined,
  };
};

/**
 * Sprint 1+2 role bridge.
 *
 * Backend now issues string roles (admin / salesperson / production / customer)
 * and includes user_type + customer_id in the JWT payload. Older tokens still
 * carry numeric role values; we accept both shapes via LEGACY_ROLE_MAP, which
 * mirrors addictive-backend/constants/roles.js.
 */

/**
 * IMPORTANT - Customer role frontend value:
 *   The pre-Sprint-1 frontend used the string 'client' for role=2 throughout
 *   pages (LeadManagement, SiteCard, RequestLeadButton, etc.). To avoid
 *   breaking those pages while also accepting the backend's new canonical
 *   string 'customer', ROLES.CUSTOMER is fixed to 'client' here and any
 *   incoming 'customer' / 2 / 'client' is normalized to 'client'.
 *   `getUserType()` independently returns 'customer' / 'internal' for the
 *   new dual-auth flow.
 */
export const ROLES = {
  ADMIN: 'admin',
  SALESPERSON: 'salesperson',
  PRODUCTION: 'production',
  CUSTOMER: 'client',
  PARTNER: 'partner',
  USER: 'user',
};

export const INTERNAL_ROLES = [ROLES.ADMIN, ROLES.SALESPERSON, ROLES.PRODUCTION];

const LEGACY_ROLE_MAP = {
  1: ROLES.ADMIN,
  2: ROLES.CUSTOMER,
  3: ROLES.SALESPERSON,
  4: ROLES.PRODUCTION,
  5: ROLES.SALESPERSON,
};

const STRING_ROLE_ALIASES = {
  client: ROLES.CUSTOMER,
  customer: ROLES.CUSTOMER,
  admin: ROLES.ADMIN,
  salesperson: ROLES.SALESPERSON,
  production: ROLES.PRODUCTION,
  partner: ROLES.PARTNER,
  user: ROLES.USER,
};

export const normalizeRole = (raw) => {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'number') {
    return LEGACY_ROLE_MAP[raw] || null;
  }
  const key = String(raw).toLowerCase();
  return STRING_ROLE_ALIASES[key] || null;
};

export const isInternalRole = (role) => INTERNAL_ROLES.includes(normalizeRole(role));

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  try {
    const decoded = jwtDecode(accessToken);
    if (!decoded || typeof decoded.exp !== 'number') return false;
    // JWT `exp` is expressed in seconds since the epoch, so it must be
    // compared against Date.now() converted to seconds. The previous check
    // compared `iat` (seconds) against Date.now() (milliseconds), which was
    // always true and let expired tokens through.
    const nowInSeconds = Date.now() / 1000;
    return decoded.exp > nowInSeconds;
  } catch (err) {
    return false;
  }
};

const decodeActiveToken = () => {
  const accessToken = getActiveToken();
  if (!accessToken) return null;
  try {
    return jwtDecode(accessToken);
  } catch (err) {
    return null;
  }
};

const isUserRole = () => {
  const overlay = getMimicPayloadOverlay();

  if (overlay) {
    const fromMimic = normalizeRole(overlay.role);
    if (fromMimic) return fromMimic;
  }

  const decoded = decodeActiveToken();
  if (!decoded) return null;
  const fromString = normalizeRole(decoded.role);
  if (fromString) return fromString;
  // Sprint 6 cutover: only canonical string roles are accepted. The legacy
  // numeric 5 -> partner mapping is gone; any unknown value falls through
  // to ROLES.USER to keep older tokens from crashing the sidebar.
  return ROLES.USER;
};

const getJwtPayload = () => {
  const decoded = decodeActiveToken();
  const overlay = getMimicPayloadOverlay();
  if (!decoded && !overlay) return null;
  // Overlay wins on identity fields so callers reading .id / .role / etc.
  // see the mimicked user; the admin's token still supplies fields the
  // mimic record doesn't carry (exp, iat, etc.).
  return { ...(decoded || {}), ...(overlay || {}) };
};

const getUserType = () => {
  const payload = getJwtPayload();
  if (!payload) return null;
  if (payload.user_type) return payload.user_type;
  const role = normalizeRole(payload.role);
  if (role === ROLES.CUSTOMER) return 'customer';
  if (INTERNAL_ROLES.includes(role)) return 'internal';
  return null;
};

const getCustomerId = () => {
  const payload = getJwtPayload();
  return payload && payload.customer_id ? payload.customer_id : null;
};

const isUser = () => getJwtPayload();

// Underlying token role, ignoring any mimic overlay. Use this to gate UI that
// belongs to the admin doing the mimicking (e.g. the mimic selector itself),
// so it stays visible after switching identity.
const getRealUserRole = () => {
  const decoded = decodeActiveToken();
  if (!decoded) return null;
  return normalizeRole(decoded.role);
};

const isMimicking = () => getMimicPayloadOverlay() !== null;

// The Authorization header is attached per-request by utils/axios.js's
// request interceptor, reading straight from localStorage. This function
// therefore only owns the storage side — no header pinning on
// axios.defaults, which would let a stale header survive after tokens are
// cleared in another tab / DevTools.
const setSession = (tokenName, accessToken) => {
  if (accessToken) {
    window.localStorage.setItem(tokenName, accessToken);
  } else {
    window.localStorage.removeItem(tokenName);
  }
};

export {
  verify,
  sign,
  isValidToken,
  setSession,
  isUserRole,
  isUser,
  getJwtPayload,
  getUserType,
  getCustomerId,
  getRealUserRole,
  isMimicking,
};
